import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {debounceTime, Subject, Subscription} from 'rxjs';
import {AiChatService} from '../../../_services/ai-chat.service';
import {AiChatConsumi, AiChatConversazione, AiChatErrore} from '../../../_models/ai-chat.model';
import {MessageConfirm} from '../../../_helpers/message-confirm.service';
import {dataUtc, GruppoConversazioni, quandoBreve, raggruppaConversazioni} from '../ai-chat-vista';
import {formattaToken} from '../ai-chat-quota';

/** Feature 159: archivio delle conversazioni dell'operatore (ricerca, gruppi per giorno, scorrimento
 * infinito, rinomina, elimina con conferma). Solo le conversazioni dell'operatore: le filtra il server. */
@Component({
  selector: 'app-ai-chat-archivio',
  templateUrl: './ai-chat-archivio.component.html',
  styleUrls: ['./ai-chat-archivio.component.scss'],
  standalone: false
})
export class AiChatArchivioComponent implements OnInit, OnDestroy {
  @Input() orgId: number;
  @Input() consumi?: AiChatConsumi;
  @Input() attivaId: string | null = null;
  @Output() apri = new EventEmitter<string>();
  @Output() nuova = new EventEmitter<void>();
  @Output() eliminata = new EventEmitter<string>();
  @Output() indietro = new EventEmitter<void>();
  @Output() chiudi = new EventEmitter<void>();

  search = '';
  conversazioni: AiChatConversazione[] = [];
  gruppi: GruppoConversazioni<AiChatConversazione>[] = [];
  totalCount = 0;
  loading = false;
  errore = '';
  inModifica: string | null = null;
  titoloModifica = '';
  readonly formattaToken = formattaToken;
  private pagina = 0;
  private richiesta = 0;
  private readonly ricerca$ = new Subject<void>();
  private subscription?: Subscription;
  private readonly aiChatService = inject(AiChatService);
  private readonly messageConfirm = inject(MessageConfirm);

  get altre(): boolean {
    return this.conversazioni.length < this.totalCount;
  }

  ngOnInit() {
    this.subscription = this.ricerca$.pipe(debounceTime(300)).subscribe(() => this.ricarica());
    this.ricarica();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  cercaCambiata() {
    this.ricerca$.next();
  }

  ricarica(): Promise<void> {
    this.pagina = 0;
    return this.carica(true);
  }

  // Scorrimento infinito: la pagina successiva parte a 120 px dal fondo.
  scorrimento(elemento: HTMLElement) {
    const vicinoAlFondo = elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 120;
    if (vicinoAlFondo && this.altre && !this.loading) {
      this.pagina++;
      void this.carica(false);
    }
  }

  meta(conversazione: AiChatConversazione): string {
    const messaggi = conversazione.messaggi === 1 ? '1 messaggio' : `${conversazione.messaggi} messaggi`;
    return `${quandoBreve(dataUtc(conversazione.updatedAtUtc))} · ${messaggi} · ${formattaToken(conversazione.totaleToken)} token`;
  }

  iniziaModifica(conversazione: AiChatConversazione) {
    this.inModifica = conversazione.id;
    this.titoloModifica = conversazione.titolo;
  }

  // Invio e blur arrivano entrambi: conta solo il primo (Esc annulla senza salvare).
  async salvaTitolo(conversazione: AiChatConversazione) {
    if (this.inModifica !== conversazione.id) return;
    const titolo = this.titoloModifica.trim();
    this.inModifica = null;
    if (!titolo || titolo === conversazione.titolo) return;
    await this.esegui(() => this.aiChatService.rinominaAsync(conversazione.id, titolo.slice(0, 120)),
      () => conversazione.titolo = titolo.slice(0, 120));
  }

  async elimina(conversazione: AiChatConversazione) {
    const confermato = await this.messageConfirm.presentCustomAlert('Elimina conversazione',
      `«${conversazione.titolo}» verrà eliminata dall'archivio. I consumi restano registrati.`, 'Elimina', 'danger', () => true);
    if (!confermato) return;
    await this.esegui(() => this.aiChatService.eliminaAsync(conversazione.id), () => this.rimuovi(conversazione.id));
  }

  traccia = (_: number, conversazione: AiChatConversazione) => conversazione.id;

  private async carica(daCapo: boolean): Promise<void> {
    const numero = ++this.richiesta;
    this.loading = true;
    try {
      const risultato = await this.aiChatService.cercaAsync(this.orgId, this.search.trim(), this.pagina);
      if (numero === this.richiesta) this.applica(risultato.result, risultato.totalCount, daCapo);
    } catch (errore) {
      if (numero === this.richiesta) this.errore = (errore as AiChatErrore).messaggio;
    } finally {
      if (numero === this.richiesta) this.loading = false;
    }
  }

  private applica(voci: AiChatConversazione[], totalCount: number, daCapo: boolean) {
    this.conversazioni = daCapo ? voci : [...this.conversazioni, ...voci];
    this.totalCount = totalCount;
    this.errore = '';
    this.gruppi = raggruppaConversazioni(this.conversazioni);
  }

  private rimuovi(id: string) {
    this.applica(this.conversazioni.filter(c => c.id !== id), this.totalCount - 1, true);
    this.eliminata.emit(id);
  }

  private async esegui(azione: () => Promise<void>, dopo: () => void) {
    try {
      await azione();
      dopo();
    } catch (errore) {
      this.errore = (errore as AiChatErrore).messaggio;
    }
  }
}
